import React from 'react'
import { DatePicker, Flex, Tabs, TimeRangePickerProps, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { AdminTab, DashboardQuery } from '../dashboard'
import { useNavigate } from '@tanstack/react-router'
import dayjs, { Dayjs } from 'dayjs'
import { RangePickerProps } from 'antd/es/date-picker'
import { AdminOverview } from './admin-overview'
import { AdminCustomers } from './admin-customers'
import { AdminSpend } from './admin-spend'

export function AdminDashboard({ start, end, tab = 'overview' }: DashboardQuery) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // Only the open tab is mounted, so only its statistics are loaded.
    return (
        <Flex
            gap="large"
            vertical
        >
            <Period
                start={start}
                end={end}
            />

            <Tabs
                activeKey={tab}
                destroyOnHidden
                onChange={(key) =>
                    navigate({
                        to: '/dashboard',
                        search: (previous) => ({ ...previous, tab: key as AdminTab }),
                        replace: true,
                    })
                }
                items={[
                    {
                        key: 'overview',
                        label: t('Overview'),
                        children: (
                            <AdminOverview
                                start={start}
                                end={end}
                            />
                        ),
                    },
                    {
                        key: 'customers',
                        label: t('Customers'),
                        children: (
                            <AdminCustomers
                                start={start}
                                end={end}
                            />
                        ),
                    },
                    {
                        key: 'spend',
                        label: t('Spend'),
                        children: (
                            <AdminSpend
                                start={start}
                                end={end}
                            />
                        ),
                    },
                ]}
            />
        </Flex>
    )
}

const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > dayjs().endOf('day')
}

export function Period({ start, end }: DashboardQuery) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    if (typeof start === 'undefined' || typeof end === 'undefined') {
        navigate({
            to: '/dashboard',
            search: (previous) => ({
                ...previous,
                start: typeof start === 'undefined' ? dayjs().subtract(90, 'days').format() : start,
                end: typeof end === 'undefined' ? dayjs().format() : end,
            }),
            replace: true,
        })

        return
    }

    let startDayjs: Dayjs | null = typeof start === 'undefined' ? null : dayjs(start)
    let endDayjs: Dayjs | null = typeof end === 'undefined' ? null : dayjs(end)

    if (startDayjs && !startDayjs.isValid()) {
        startDayjs = null
    }

    if (endDayjs && !endDayjs.isValid()) {
        endDayjs = null
    }

    const rangePresets: TimeRangePickerProps['presets'] = [
        { label: t('Last 7 Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
        { label: t('Last 14 Days'), value: [dayjs().add(-14, 'd'), dayjs()] },
        { label: t('Last 30 Days'), value: [dayjs().add(-30, 'd'), dayjs()] },
        { label: t('Last 90 Days'), value: [dayjs().add(-90, 'd'), dayjs()] },
        { label: t('This Month'), value: [dayjs().startOf('month'), dayjs()] },
        { label: t('All time'), value: [dayjs('2025-01-01'), dayjs()] },
    ]

    return (
        <Flex
            justify="space-between"
            align="center"
            className="flex-col md:flex-row"
        >
            <Typography.Title level={3}>{t('Statistics for period')}</Typography.Title>
            <DatePicker.RangePicker
                presets={rangePresets}
                disabledDate={disabledDate}
                value={[startDayjs, endDayjs]}
                onChange={(range) => {
                    if (range === null || range[0] === null || range[1] === null) {
                        return navigate({
                            to: '/dashboard',
                            search: (previous) => ({
                                ...previous,
                                start: undefined,
                                end: undefined,
                            }),
                            replace: true,
                        })
                    }

                    const [start, end] = range

                    navigate({
                        to: '/dashboard',
                        search: (previous) => ({
                            ...previous,
                            start: start.format(),
                            end: end.format(),
                        }),
                        replace: true,
                    })
                }}
            />
        </Flex>
    )
}
